'use client';

import type { PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname } from 'next/navigation';
import { useEffect, useState, type MouseEvent } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { decodeHTML } from 'entities';
import {
  buildCategoryPaginationHref,
  getCategoryPaginationLinkPage,
  getCategoryPaginationNavigation,
  getCategoryPaginationPage,
} from '@/utils/category-pagination';

type Props = {
  links?: PaginationMeta['links'];
  search?: string;
  onPageChange: (search: string) => void;
  scrollToElementAfterPageChange?: string;
  disabled?: boolean;
};

/** A client-owned product list needs real links, not another server route request. */
export function CategoryPagination(props: Props) {
  const pathname = usePathname();
  const breakpoint = useBreakpoint('sm');
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const links = props.links ?? [];
  const requestedPage =
    getCategoryPaginationPage(new URLSearchParams(props.search).get('page')) ?? 1;
  const lastKnownPage = Math.max(
    1,
    ...links.slice(1, -1).map(link => getCategoryPaginationLinkPage(link.url) ?? 0),
  );
  const activeIndex = links.findIndex(
    (link, index) =>
      index > 0 &&
      index < links.length - 1 &&
      getCategoryPaginationLinkPage(link.url) === requestedPage,
  );
  const firstMobileIndex =
    activeIndex <= 2 ? 1 : activeIndex >= links.length - 4 ? links.length - 6 : activeIndex - 2;
  const lastMobileIndex =
    activeIndex <= 2 ? 5 : activeIndex >= links.length - 4 ? links.length - 2 : activeIndex + 2;

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }
    const navigation = getCategoryPaginationNavigation(
      window.location.href,
      event.currentTarget.href,
      {
        button: event.button,
        defaultPrevented: event.defaultPrevented,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        target: event.currentTarget.target,
        download: event.currentTarget.hasAttribute('download'),
      },
    );
    if (!navigation) return;
    event.preventDefault();
    if (!navigation.changed) return;
    // Let Next's native-history integration copy its tree and notify URL hooks.
    // Passing the existing __NA state would bypass that integration.
    window.history.pushState(null, '', navigation.href);
    props.onPageChange(navigation.search);

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    const id = props.scrollToElementAfterPageChange;
    if (id && id !== '__top__') document.getElementById(id)?.scrollIntoView({ behavior });
    else window.scrollTo({ top: 0, behavior });
  };

  return (
    <nav className="ea-pagination flex flex-row items-center space-x-2">
      {links.map((link, index) => {
        if (
          isMounted &&
          !breakpoint &&
          index !== 0 &&
          index !== links.length - 1 &&
          (index < firstMobileIndex || index > lastMobileIndex)
        )
          return null;
        const page =
          index === 0
            ? requestedPage > 1
              ? requestedPage - 1
              : undefined
            : index === links.length - 1
              ? requestedPage < lastKnownPage
                ? requestedPage + 1
                : undefined
              : getCategoryPaginationLinkPage(link.url);
        const active = index > 0 && index < links.length - 1 && page === requestedPage;
        const href =
          page && pathname
            ? buildCategoryPaginationHref(pathname, props.search ?? '', page)
            : undefined;
        const content =
          index === 0 ? (
            <LuChevronLeft className="h-4 w-4 shrink-0" />
          ) : index === links.length - 1 ? (
            <LuChevronRight className="h-4 w-4 shrink-0" />
          ) : (
            link.label
          );
        return (
          <Button
            key={`${link.label}-${link.url}-${index}`}
            size="none"
            aria-label={
              index === 0
                ? 'Predchádzajúca stránka'
                : index === links.length - 1
                  ? 'Nasledujúca stránka'
                  : `Stránka ${link.label}`
            }
            className={cn('col-span-1 h-9 w-9 p-1', 'ea-pagination__item')}
            asChild={!!href && !props.disabled}
            disabled={!href || props.disabled}
            variant={active ? 'default' : !href ? 'ghost' : 'secondary'}
          >
            {href && !props.disabled ? (
              <a
                href={href}
                onClick={onClick}
                aria-current={active ? 'page' : undefined}
                aria-label={
                  index === 0 || index === links.length - 1 ? decodeHTML(link.label) : undefined
                }
              >
                {content}
              </a>
            ) : (
              content
            )}
          </Button>
        );
      })}
    </nav>
  );
}
